import type { ReportAudienceDto, ReportAudienceSave } from "./reportSharingModel.ts";
export type { ReportAudienceDto, ReportAudienceSave } from "./reportSharingModel.ts";
/** Shared named-recipient editor. The host retains its session/definition fence. */
export function mountReportSharing(host:HTMLElement, options:{
  audience:ReportAudienceDto;
  isCurrent:()=>boolean;
  choices:()=>Promise<{items?:Array<{id:string;name:string}>;error?:string}>;
  save:(input:ReportAudienceSave)=>Promise<string|undefined>;
  done:()=>void;
}):void {
  if(host.querySelector("[data-report-sharing]")!==null)return;
  const root=document.createElement("dialog"); root.className="cr-panel";root.dataset["reportSharing"]="true";
  const heading=document.createElement("h3");heading.textContent="Sharing";
  const mode=document.createElement("select");mode.className="cr-select";mode.setAttribute("aria-label","Report visibility");
  for(const [value,text] of [["","Choose visibility"],["private","Private"],["named","Share with"]]) {
    const option=document.createElement("option");option.value=value!;option.textContent=text!;mode.append(option);
  }
  mode.value=options.audience.mode==="private"||options.audience.mode==="named"?options.audience.mode:"";
  const people=document.createElement("fieldset");
  const legend=document.createElement("legend");legend.textContent="People";people.append(legend);
  const status=document.createElement("p");status.setAttribute("role","status");status.textContent="Loading";
  const save=document.createElement("button");save.type="button";save.className="cr-btn-primary";save.textContent="Save";
  const cancel=document.createElement("button");cancel.type="button";cancel.className="cr-btn-quiet";cancel.textContent="Cancel";
  const invoking=document.activeElement;
  const dismiss=()=>{observer.disconnect();root.close();root.remove();if(options.isCurrent()&&invoking instanceof HTMLElement&&invoking.isConnected)invoking.focus();};
  const observer=new MutationObserver(()=>{if(!root.isConnected||!options.isCurrent())dismiss();});
  observer.observe(host,{childList:true,subtree:true});
  const selected=new Set(options.audience.recipientIds);
  let ready=false;let busy=false;
  const update=()=>{people.hidden=mode.value!=="named";people.disabled=busy;mode.disabled=busy;cancel.disabled=busy;save.disabled=!ready||busy||mode.value===""||(mode.value==="named"&&selected.size===0);};
  mode.onchange=update;
  cancel.onclick=()=>{if(!busy)dismiss();};
  root.addEventListener("cancel",event=>{event.preventDefault();if(!busy)dismiss();});
  save.onclick=()=>{void (async()=>{
    if(save.disabled||!options.isCurrent()||(mode.value!=="private"&&mode.value!=="named"))return;
    busy=true;status.textContent="Saving";update();
    let error:string|undefined;
    try { error=await options.save({mode:mode.value,recipientIds:mode.value==="named"?[...selected]:[],baseAudienceRevision:options.audience.revision}); }
    catch { error="That change did not save."; }
    if(!options.isCurrent()){dismiss();return;}
    busy=false;
    if(error!==undefined){status.textContent=error;update();return;}
    dismiss();options.done();
  })();};
  root.append(heading,mode,people,status,save,cancel);host.append(root);root.showModal();update();
  void options.choices().then(result=>{
    if(!options.isCurrent()){dismiss();return;}
    if(result.items===undefined){status.textContent=result.error??"People could not load.";return;}
    const eligible=new Set(result.items.map(person=>person.id));
    for(const id of selected)if(!eligible.has(id))selected.delete(id);
    for(const person of result.items){
      const label=document.createElement("label");label.className="cr-check";
      const input=document.createElement("input");input.type="checkbox";input.checked=selected.has(person.id);
      input.onchange=()=>{if(!busy&&options.isCurrent()){if(input.checked)selected.add(person.id);else selected.delete(person.id);update();}};
      label.append(input,document.createTextNode(person.name));people.append(label);
    }
    ready=true;status.textContent="";update();
  }).catch(()=>{if(options.isCurrent())status.textContent="People could not load.";});
}
